import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  isSupportedLanguage,
  LANGUAGE_LABELS,
  LANGUAGE_TAGS,
  localizedPath,
  parseLocalizedPath,
  SUPPORTED_LANGUAGES,
  type Language,
} from '@lifetimeline/shared';

import { useAuth } from '../features/auth/AuthContext.js';
import { storeLanguage, useI18n } from '../i18n/index.js';
import { CONTACT_EMAIL } from '../lib/branding.js';
import { Avatar } from './Avatar.js';
import { Button } from './Button.js';
import { CogIcon, SearchIcon } from './icons.js';
import styles from './Layout.module.css';

/** 화면마다 감싸는 셸. 헤더·본문 폭·푸터를 한 곳에서 정한다. */
export function Layout({ children, narrow = false }: { children: ReactNode; narrow?: boolean }) {
  const { t, language } = useI18n();
  const privacyPath = localizedPath(language, '/privacy');

  return (
    <div className={styles.shell}>
      {/* 키보드로 Tab을 처음 누르면 나타난다 — 헤더를 매번 지나치지 않게 */}
      <a className="skipLink" href="#main">
        {t('nav.skipToContent')}
      </a>
      <Header />
      <main id="main" className={styles.main}>
        <div className={[styles.content, narrow ? styles.narrow : ''].filter(Boolean).join(' ')}>
          {children}
        </div>
      </main>
      {/*
        왼쪽에 이름, 오른쪽에 정보 칸. 정보가 늘어도(사업자 정보, 약관…)
        줄만 더하면 되도록 `dl`로 둔다 — 라벨과 값이 짝이라는 것이 구조에
        들어 있어야 나중에 줄을 더할 때 모양을 다시 안 짠다.
      */}
      <footer className={styles.foot}>
        {/* 같은 이름을 같은 글씨로. 한 화면에서 로고가 두 벌로 보이면 안 된다 */}
        <strong className={styles.wordmark} data-hangul={hasHangul(t('common.appName'))}>
          {t('common.appName')}
        </strong>

        <dl className={styles.footInfo}>
          <div className={styles.footRow}>
            <dt className={styles.footLabel}>{t('common.email')}</dt>
            {/*
              막혔거나 아쉬운 게 있을 때 **갈 데가 한 군데는 있어야 한다.**
            */}
            <dd className={styles.footValue}>
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </dd>
          </div>
          {/*
            개인정보처리방침은 **닿을 수 있는 곳**에 있어야 한다 — 구글이
            OAuth 앱 게시 조건으로 요구하는 것도 "공개된 링크"다.
          */}
          <div className={styles.footRow}>
            <dt className={styles.footLabel}>{t('legal.privacyTitle')}</dt>
            <dd className={styles.footValue}>
              <Link to={privacyPath}>life-timeline.com{privacyPath}</Link>
            </dd>
          </div>
        </dl>
      </footer>
    </div>
  );
}

/**
 * 로고에 픽셀 글씨(Silkscreen)를 쓸 수 있는 이름인가.
 *
 * ⚠️ Silkscreen에는 **한글이 없다.** `라타라`를 그 글씨로 지정하면 글자마다
 * 조용히 다른 글씨로 떨어져서, 픽셀도 아니고 본문도 아닌 것이 나온다.
 * 그래서 한글 이름은 본문 글씨로 찍고, 로마자 이름만 픽셀로 찍는다.
 *
 * 언어가 아니라 **글자**를 본다 — 나중에 일본어를 더해도 그 이름이 로마자면
 * 픽셀로 나가야 맞다.
 */
function hasHangul(name: string): boolean {
  return /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(name);
}

/** 로그인 흐름 안에서는 헤더 액션이 흐름을 흐린다 */
const AUTH_ROUTES = new Set(['/signin', '/signup']);

function Header() {
  const { t, language, setLanguage } = useI18n();
  const { status, profile } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /*
   * ⚠️ **확인 중(`loading`)도 로그인한 쪽으로 친다.**
   *
   * `loading`은 토큰이 있을 때만 나온다(AuthContext) — 즉 이 사람은 거의
   * 확실히 로그인 상태고, 서버에 물어보는 중일 뿐이다. 그런데 예전에는
   * `authenticated`만 로그인으로 쳐서, 새로고침할 때마다 헤더가
   * **로그인 버튼 → 돋보기+내 사진**으로 한 번 뒤집혔다. 토큰이 죽어 있는
   * 드문 경우에만 반대로 뒤집히는데, 그건 매번 깜빡이는 것보다 낫다.
   */
  const signedIn = status === 'authenticated' || status === 'loading';

  /*
   * 헤더에는 **둘**만 둔다 — 남을 찾는 돋보기와 나를 가리키는 내 사진.
   *
   * 예전에는 타임라인·사람·픽 세 아이콘이었다. 타임라인과 픽은 이제 홈의
   * 탭이라 헤더에서 갈 이유가 없어졌고, 남은 것은 "남에게 가는 길"과
   * "나에게 오는 길" 둘이다. 내 사진을 쓰는 이유는 그 자리가 **내 계정**을
   * 뜻한다는 것을 글자 없이 말해 주기 때문이다 — 어느 계정으로 들어와
   * 있는지도 같이 보인다.
   *
   * **이미 내 프로필에 있으면 그 자리가 설정이 된다.** 내 사진을 눌러 봐야
   * 지금 보고 있는 화면으로 다시 올 뿐이라 빈 버튼이 되는데, 그 자리에서
   * 다음으로 갈 곳은 설정이다.
   */
  const onMyProfile = pathname === '/me';

  /*
   * 언어별 화면(`/en`, `/ko/privacy`)에서는 언어를 바꾸면 **주소도 옮긴다.**
   * 주소가 곧 언어라서, 화면만 한국어로 바꾸고 주소를 `/en`에 두면 그 주소를
   * 복사해 간 사람은 영어를 받는다.
   */
  const changeLanguage = (next: Language) => {
    const localized = parseLocalizedPath(pathname);
    if (localized === null) {
      setLanguage(next);
      return;
    }
    /*
     * 언어는 **주소가 바꾼다**(`LocalizedRoute`). 여기서 같이 바꾸면 라우터의
     * 이동이 한 박자 늦게(transition) 반영되는 사이에 옛 주소의 화면이 언어를
     * 도로 돌려놓는다. 사람이 고른 것이니 저장만 먼저 한다.
     */
    storeLanguage(next);
    navigate(localizedPath(next, localized.page), { replace: true });
  };

  return (
    <header className={styles.top}>
      {/* 로고는 관례대로 홈으로. 여기서 벗어나면 돌아갈 길을 찾게 만든다 */}
      <Link
        to={localizedPath(language, '')}
        className={`${styles.wordmark} ${styles.brand}`}
        data-hangul={hasHangul(t('common.appName'))}
      >
        {t('common.appName')}
      </Link>

      <nav className={styles.nav}>
        <LanguageSwitch language={language} onChange={changeLanguage} />

        {/*
          로그인 화면에서만 그리지 않는다 — 제목이 이미 "로그인"이라 같은 말이
          두 번 보이고, 가입 도중에 로그인으로 되돌리는 버튼은 흐름을 흐린다.
        */}
        {AUTH_ROUTES.has(pathname) ? null : signedIn ? (
          <>
            <Button
              variant="quiet"
              size="sm"
              className={styles.iconAction}
              aria-label={t('people.title')}
              aria-current={pathname === '/people' ? 'page' : undefined}
              title={t('people.title')}
              onClick={() => navigate('/people')}
            >
              <SearchIcon size={19} />
            </Button>
            {onMyProfile ? (
              <Button
                variant="quiet"
                size="sm"
                className={styles.iconAction}
                aria-label={t('nav.settings')}
                aria-current={undefined}
                title={t('nav.settings')}
                onClick={() => navigate('/settings')}
              >
                <CogIcon size={19} />
              </Button>
            ) : (
              <button
                type="button"
                className={styles.avatarAction}
                aria-label={t('home.profile')}
                title={t('home.profile')}
                onClick={() => navigate('/me')}
              >
                <Avatar name={profile?.userName ?? ''} imageUrl={profile?.imageUrl} size={28} />
              </button>
            )}
          </>
        ) : (
          /*
            글자 그대로 둔다 — 아이콘으로 그리면 무슨 뜻인지 짐작하게 만든다.
            `로그인`이 아니라 **`시작하기`**인 이유: 남의 프로필을 보고 온
            사람에게 필요한 말은 "들어와라"가 아니라 "너도 만들어라"다.
            `로그인`은 이미 계정이 있는 사람의 말이고, 여기 서 있는 사람은
            대개 계정이 없다.

            ⚠️ 랜딩의 큰 버튼(`landing.start`)과 **같은 값**을 본다. 따로
            적어 두면 한쪽만 고쳐져 같은 화면에서 두 말이 된다.
          */
          <Button variant="quiet" size="sm" onClick={() => navigate('/signin')}>
            {t('landing.start')}
          </Button>
        )}
      </nav>
    </header>
  );
}

/** 헤더에 보이는 짧은 표시. 중국어는 코드(`ZH-CN`)보다 글자 체계가 바로 읽힌다 */
const LANGUAGE_SHORT: Record<Language, string> = {
  ko: 'KO',
  en: 'EN',
  ja: 'JA',
  'zh-cn': '简体',
  'zh-tw': '繁體',
  es: 'ES',
  pt: 'PT',
};

/**
 * 언어 고르기. 일곱 개를 버튼으로 늘어놓으면 휴대폰 헤더에 안 들어가서
 * **기기 기본 선택 창**을 쓴다 — 휴대폰에서는 아래에서 올라오는 목록이 뜨고,
 * 목록에는 각 언어가 그 언어 이름(`日本語`)으로 보인다.
 *
 * 보이는 것은 짧은 표시(`EN ▾`)이고, 진짜 `<select>`는 그 위에 투명하게
 * 겹쳐 둔다. 닫힌 `<select>`는 고른 항목의 긴 이름(`Português (Brasil)`)을
 * 그대로 보여 줘서 헤더가 밀린다.
 */
function LanguageSwitch({
  language,
  onChange,
}: {
  language: Language;
  onChange: (next: Language) => void;
}) {
  return (
    <label className={styles.langSwitch}>
      <span className={styles.langCurrent} aria-hidden="true">
        {LANGUAGE_SHORT[language]}
        <span className={styles.langChevron}>▾</span>
      </span>
      <select
        className={styles.langSelect}
        aria-label="Language"
        value={language}
        onChange={(event) => {
          const next = event.target.value;
          if (isSupportedLanguage(next)) {
            onChange(next);
          }
        }}
      >
        {SUPPORTED_LANGUAGES.map((code) => (
          <option key={code} value={code} lang={LANGUAGE_TAGS[code]}>
            {LANGUAGE_LABELS[code]}
          </option>
        ))}
      </select>
    </label>
  );
}
